import React from 'react';
import { Link } from 'react-router-dom';
import { ScrollText } from 'lucide-react';
import { ValidationBadge } from './ValidationBadge';
import { QuestSoloNote } from './quest/QuestSoloNote';
import { coordLabel, monsterPath, spotPath } from '../../utils/worldFormat';
import type { QuestRoute } from '../../utils/progressionLookup';
const SHOW = 6;

/**
 * Drops de misión documentados. Dependen de una misión activa y de sus condiciones:
 * no son drops generales disponibles para cualquier jugador.
 */
export function QuestDropSummary({
  quest
}: {
  quest: QuestRoute[];
}) {
  const targets = quest.flatMap(q => q.targets);
  const unique = Array.from(new Map(targets.map(t => [`${t.monsterId}|${t.mapId ?? ''}`, t])).values());
  const mapsCount = new Set(unique.map(t => t.mapId).filter(m => m !== undefined)).size;
  const conditions = Array.from(new Set(quest.map(q => q.route.conditions).filter(Boolean))) as string[];
  const variants = Array.from(new Set(quest.map(q => q.variantName).filter(Boolean))) as string[];
  return <div className="mt-2 rounded-xl border border-emerald-300/20 bg-emerald-300/[0.04] p-3" data-id="element-1277" data-mp="1u5j1da7ge7l3">
      <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-white" data-id="element-1278" data-mp="1pbn4yc6u8ss1">
        <ScrollText className="h-4 w-4 text-emerald-200" aria-hidden="true" data-id="element-1279" data-mp="1kewqftaom58q" />
        Se obtiene durante la misión
        <ValidationBadge status="documentado" label="Ruta de misión documentada" data-id="element-1280" data-mp="1fgv6l65fm1w7" />
      </p>
      <p className="mt-1 text-xs text-amber-100" data-id="element-1281" data-mp="2d2ae10shpog6">Requiere tener la misión activa{conditions.length ? `: ${conditions.join(' · ')}` : '.'} No es un drop general.</p>
      <QuestSoloNote data-id="element-1282" data-mp="23438de3nisth" />
      {variants.length > 0 && <p className="mt-1 text-xs text-gray-400" data-id="element-1283" data-mp="232tbutcg5soy">Variante: {variants.join(', ')}</p>}
      {unique.length > 0 ? <>
          <p className="mt-2 text-xs text-gray-400" data-id="element-1284" data-mp="rphf12f3uaca">
            {unique.length} {unique.length === 1 ? 'monstruo' : 'combinaciones de monstruo y mapa'}
            {mapsCount ? ` en ${mapsCount} ${mapsCount === 1 ? 'mapa' : 'mapas'}` : ''}. Posibles, no garantizados.
          </p>
          <ul className="mt-1.5 flex flex-wrap gap-1.5" data-id="element-1285" data-mp="p2ymhyq89b9p">
            {unique.slice(0, SHOW).map(t => {
          const spot = t.spawns.length === 1 ? t.spawns[0] : undefined;
          return <li key={`${t.monsterId}-${t.mapId ?? ''}`} data-id="element-1286" data-mp="f980r1lhjexp">
                  <Link to={spot ? spotPath(spot.id) : monsterPath(t.monsterId)} className="inline-flex min-h-[32px] items-center gap-1 rounded-full border border-white/15 px-3 py-1 text-xs text-gray-200 transition-colors duration-150 hover:border-[#D4AF37]/50 hover:text-white" data-id="element-1287" data-mp="k4dh7kw4c1ee">
                    <span className="font-semibold" data-id="element-1288" data-mp="1fgnbi4mvawp">{t.monsterName}</span>
                    {t.mapName && <span className="text-gray-400" data-id="element-1289" data-mp="1d47pbmclcmt">· {t.mapName}</span>}
                    {spot && coordLabel(spot) && <span className="font-mono text-[11px] text-gray-500" data-id="element-1290" data-mp="3jnfvf5lb11sf">· {coordLabel(spot)}</span>}
                  </Link>
                </li>;
        })}
          </ul>
          {unique.length > SHOW && <p className="mt-1 text-xs text-gray-500" data-id="element-1291" data-mp="2zz9zpljijqbs">+{unique.length - SHOW} más en “Dónde conseguirlo”.</p>}
        </> : <p className="mt-1 text-xs text-gray-400" data-id="element-1292" data-mp="3dzb6tiqdftzp">Monstruos de la ruta de misión pendientes de documentar.</p>}
    </div>;
}